// src/services/apiService.js

// Determine API base URL based on environment. CRA loads .env.development
// for `npm start` and .env.production for `npm run build` automatically, so
// REACT_APP_API_BASE_URL switches on its own — no manual toggling needed.
const getApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_BASE_URL;
  if (configured) {
    // Every endpoint string below already includes its own `/api/...`
    // prefix, so this must resolve to the bare origin — strip a trailing
    // /api from the env value if it was included there too.
    return configured.replace(/\/api\/?$/, '');
  }
  return process.env.NODE_ENV === 'development'
    ? 'https://scaner-backend.onrender.com'
    : 'https://scaner-backend.onrender.com';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    console.log('API Service initialized with base URL:', this.baseURL);
  }

  // Get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  // Generic API call method with enhanced error handling
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getAuthHeaders();
    
    console.log('Making API request:', {
      url,
      method: options.method || 'GET',
      headers: headers
    });
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
        // Add CORS configuration for non-proxy requests
        ...(this.baseURL.startsWith('http') && {
          mode: 'cors',
          credentials: 'omit'
        })
      });

      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url
      });

      // Handle non-JSON responses
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const textData = await response.text();
        console.log('Non-JSON response received:', textData);
        
        // Try to parse as JSON anyway (some APIs don't set correct content-type)
        try {
          data = JSON.parse(textData);
        } catch {
          data = { message: textData };
        }
      }

      if (!response.ok) {
        console.error('API Error Response:', {
          status: response.status,
          data: data
        });

        // Handle specific HTTP errors
        if (response.status === 401) {
          // Handle unauthorized - trigger logout
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          throw new Error('Session expired. Please login again.');
        }
        if (response.status === 403) {
          throw new Error('Access forbidden. Insufficient permissions.');
        }
        if (response.status === 404) {
          throw new Error('Resource not found.');
        }
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('API Response Success:', data);
      return data;
    } catch (error) {
      console.error('API Request Error:', {
        url,
        error: error.message,
        stack: error.stack
      });
      
      // Handle different types of errors
      if (error.name === 'TypeError') {
        if (error.message.includes('fetch')) {
          throw new Error('Unable to connect to server. Please check your connection and server status.');
        }
        if (error.message.includes('CORS')) {
          throw new Error('Cross-origin request blocked. Please check server CORS configuration.');
        }
        if (error.message.includes('Content Security Policy')) {
          throw new Error('Request blocked by Content Security Policy. Please check your configuration.');
        }
      }
      
      throw error;
    }
  }

  // Test connection method
  async testConnection() {
    try {
      console.log('Testing connection to:', this.baseURL);
      
      // Try a simple GET request first (if you have a health check endpoint)
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        ...(this.baseURL.startsWith('http') && {
          mode: 'cors',
          credentials: 'omit'
        })
      });
      
      console.log('Health check response:', response.status);
      return { success: true, status: response.status };
    } catch (error) {
      console.error('Connection test failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Authentication APIs - Fixed endpoint based on actual server response
  async login(credentials) {
    console.log('API Service: Attempting login with baseURL:', this.baseURL);
    
    // Based on the error, let's try the correct endpoint from your Postman
    // The error shows the server exists but /admin/login doesn't
    // From your Postman screenshot, it looks like the endpoint might be different
    
    const possibleEndpoints = [
      '/admin/login',     // Current attempt (fails)
      '/login',           // Simple login
      '/auth/login',      // Auth login  
      '/api/admin/login', // Maybe nested API
      '/user/login',      // User login
      '/admin/auth',      // Admin auth
      '/authentication',  // Full word
    ];
    
    let lastError = null;
    
    for (const endpoint of possibleEndpoints) {
      try {
        console.log(`Trying endpoint: ${this.baseURL}${endpoint}`);
        
        const result = await this.makeRequest(endpoint, {
          method: 'POST',
          body: JSON.stringify(credentials),
        });
        
        console.log(`Success with endpoint: ${endpoint}`);
        return result;
        
      } catch (error) {
        console.log(`Failed with endpoint ${endpoint}:`, error.message);
        lastError = error;
        
        // If it's a server error (500) or auth error (401), don't try other endpoints
        if (error.message.includes('500') || error.message.includes('401') || error.message.includes('Unauthorized')) {
          throw error;
        }
        
        // Continue trying if it's 404/not found
        continue;
      }
    }
    
    // If all endpoints failed with 404, provide helpful error
    throw new Error(
      `Login endpoint not found. The server is reachable but none of the common login endpoints exist. ` +
      `Please check your backend API documentation for the correct login endpoint. ` +
      `Last error: ${lastError.message}`
    );
  }

  async logout() {
    return this.makeRequest('/admin/logout', {
      method: 'POST',
    });
  }


  async register(userData) {
  console.log('API Service: Attempting registration');
  
  return this.makeRequest('/api/users/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

  async verifyToken() {
    return this.makeRequest('/admin/verify', {
      method: 'GET',
    });
  }

  async changePassword(passwordData) {
    return this.makeRequest('/admin/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  }

  // User Management APIs
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/users/officers${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint, {
      method: 'GET',
    });
  }
  async makePostRequest(endpoint, data = {}) {
  const url = `${this.baseURL}${endpoint}`;
  const headers = this.getAuthHeaders();
  
  console.log('Making POST API request:', {
    url,
    method: 'POST',
    headers: headers,
    data: data
  });
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
      // Add CORS configuration for non-proxy requests
      ...(this.baseURL.startsWith('http') && {
        mode: 'cors',
        credentials: 'omit'
      })
    });

    console.log('Response received:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url
    });

    // Handle non-JSON responses
    let responseData;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      const textData = await response.text();
      console.log('Non-JSON response received:', textData);
      
      // Try to parse as JSON anyway (some APIs don't set correct content-type)
      try {
        responseData = JSON.parse(textData);
      } catch {
        responseData = { message: textData };
      }
    }

    if (!response.ok) {
      console.error('API Error Response:', {
        status: response.status,
        data: responseData
      });

      // Handle specific HTTP errors
      if (response.status === 401) {
        // Handle unauthorized - trigger logout
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        throw new Error('Session expired. Please login again.');
      }
      if (response.status === 403) {
        throw new Error('Access forbidden. Insufficient permissions.');
      }
      if (response.status === 404) {
        throw new Error('Resource not found.');
      }
      if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      }
      
      throw new Error(responseData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('API Response Success:', responseData);
    return responseData;
  } catch (error) {
    console.error('API Request Error:', {
      url,
      error: error.message,
      stack: error.stack
    });
    
    // Handle different types of errors
    if (error.name === 'TypeError') {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your connection and server status.');
      }
      if (error.message.includes('CORS')) {
        throw new Error('Cross-origin request blocked. Please check server CORS configuration.');
      }
      if (error.message.includes('Content Security Policy')) {
        throw new Error('Request blocked by Content Security Policy. Please check your configuration.');
      }
    }
    
    throw error;
  }
}

  async registerEmployee(employeeData = {}) {
  const endpoint = `/api/admin/register-employee`;
  console.log('registerEmployee called with:', employeeData);
  return this.makePostRequest(endpoint, employeeData);  // ✅ Correct
}

  async getUser(userId) {
    return this.makeRequest(`/api/users/${userId}`, {
      method: 'GET',
    });
  }

  // Officer edit/delete — matches PUT/DELETE /api/users/:id on the backend.
  async updateUser(userId, userData) {
    return this.makeRequest(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId) {
    return this.makeRequest(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Employee Management APIs
  async getEmployees(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/admin/employees${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint, {
      method: 'GET',
    });
  }

  async getEmployee(employeeId) {
    return this.makeRequest(`/admin/employees/${employeeId}`, {
      method: 'GET',
    });
  }

  async createEmployee(employeeData) {
    return this.makeRequest('/admin/employees', {
      method: 'POST',
      body: JSON.stringify(employeeData),
    });
  }

  async updateEmployee(employeeId, employeeData) {
    return this.makeRequest(`/admin/employees/${employeeId}`, {
      method: 'PUT',
      body: JSON.stringify(employeeData),
    });
  }

  async deleteEmployee(employeeId) {
    return this.makeRequest(`/admin/employees/${employeeId}`, {
      method: 'DELETE',
    });
  }

  // Scan Management APIs
  async getScans(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/scans/scan-log${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint, {
      method: 'GET',
    });
  }

  async getScan(scanId) {
    return this.makeRequest(`/admin/scans/${scanId}`, {
      method: 'GET',
    });
  }

  // GET /scans/:id/photo requires a Bearer header, which plain <img src> can't
  // send — fetch it as an authenticated blob and hand back an object URL.
  async fetchScanPhotoBlob(scanId) {
    const response = await fetch(`${this.baseURL}/api/scans/${scanId}/photo`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to load photo');
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // Full timeline for one physical object — the registration plus every
  // verification attempt matched against it. scanId can be any log in the
  // chain (the registration itself or any single verification attempt).
  async getScanLogChain(scanId) {
    return this.makeRequest(`/api/scans/${scanId}/chain`, {
      method: 'GET',
    });
  }

  async createScan(scanData) {
    return this.makeRequest('/admin/scans', {
      method: 'POST',
      body: JSON.stringify(scanData),
    });
  }

  async updateScan(scanId, scanData) {
    return this.makeRequest(`/admin/scans/${scanId}`, {
      method: 'PUT',
      body: JSON.stringify(scanData),
    });
  }

  async deleteScan(scanId) {
    return this.makeRequest(`/admin/scans/${scanId}`, {
      method: 'DELETE',
    });
  }

  async verifyScan(scanId, verificationData) {
    return this.makeRequest(`/admin/scans/${scanId}/verify`, {
      method: 'POST',
      body: JSON.stringify(verificationData),
    });
  }

  async rejectScan(scanId, rejectionData) {
    return this.makeRequest(`/admin/scans/${scanId}/reject`, {
      method: 'POST',
      body: JSON.stringify(rejectionData),
    });
  }

  // Dashboard/Analytics APIs
  async getDashboardStats() {
    return this.makeRequest('/admin/dashboard/stats', {
      method: 'GET',
    });
  }

  async getReports(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/admin/reports${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint, {
      method: 'GET',
    });
  }

  async generateReport(reportConfig) {
    return this.makeRequest('/admin/reports/generate', {
      method: 'POST',
      body: JSON.stringify(reportConfig),
    });
  }

  // Settings APIs
  async getSettings() {
    return this.makeRequest('/admin/settings', {
      method: 'GET',
    });
  }

  async updateSettings(settingsData) {
    return this.makeRequest('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  // File Upload API
  async uploadFile(file, endpoint = '/admin/upload') {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
        ...(this.baseURL.startsWith('http') && {
          mode: 'cors',
          credentials: 'omit'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'File upload failed');
      }

      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  }

  // Bulk Operations
  async bulkDelete(endpoint, ids) {
    return this.makeRequest(`${endpoint}/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  async bulkUpdate(endpoint, updates) {
    return this.makeRequest(`${endpoint}/bulk-update`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Search APIs
  async search(query, filters = {}) {
    const searchParams = new URLSearchParams({
      q: query,
      ...filters,
    }).toString();
    
    return this.makeRequest(`/admin/search?${searchParams}`, {
      method: 'GET',
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Export the class for testing or multiple instances if needed
export { ApiService };
